import useFilters from 'ext-ui/hooks/filters/filters'
import React, { ReactElement, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import {
  selectCreditCardStatisticsActiveVcnCount,
  selectCreditCardStatisticsVcnCount,
} from 'src/app/Account/redux/account.selectors'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import type {
  VirtualCardsFilters,
  VirtualCardsFiltersBody,
} from 'src/components/VirtualCardsBoxAndLinks/virtualCardsFilter.schema'
import {
  makeBaseVirtualCardsSchema,
  VirtualCardsListContext,
} from 'src/components/VirtualCardsBoxAndLinks/virtualCardsFilter.schema'
import CardVirtualCardsTable from 'src/feature/CardVirtualCardsTable/CardVirtualCardsTable'
import { useWindowSize } from 'src/hooks'
import { createVirtualCardUrl, virtualCardDetailUrl } from 'src/navigation/urls'
import { useQueryCreditCardPermissions } from 'src/queries/creditCard.queries'
import { useQueryVirtualCards } from 'src/queries/virtualCards.queries'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'

/** Matches the legacy tab's page size, so paging behaviour is unchanged. */
const VIRTUAL_CARDS_PER_PAGE = 50

/**
 * The grid fills the viewport below the page chrome. The same 130 the legacy
 * tab used — this surface has the same chrome above the table now that the
 * filter row an earlier revision invented is gone.
 */
const PAGE_CHROME_HEIGHT = 130

interface AccountVirtualCardsTabProps {
  /** The card whose virtual cards to list. */
  creditCardId: string
}

/**
 * Redesigned Virtual Cards tab content for the card-detail page (EX-40736).
 *
 * Replaces the legacy `AccountVirtualCards` (`src/app/`) when
 * `isUpdatedVirtualCardsTableEnabled` is on.
 *
 * The tab is ONE table under a heading, per the design prototype
 * (`extend-design-render/prototypes/rcn-card-greenfield`, `VirtualCardsTab` in
 * `components/budgets-vcards.tsx`): the section title, an "N active · M total"
 * count, a New virtual card button, and the rows. That prototype is the spec —
 * read it before changing anything visual here.
 *
 * Deliberately NOT carried over from legacy:
 *  - The search field, the filter flyout, and the CSV export. The design has no
 *    filter chrome on this tab at all, and an earlier revision of this branch
 *    invented a row of per-dimension filter buttons that matched nothing.
 *  - `ObjectBoxTitle` and its "Virtual Cards Created / Active N" pair, replaced
 *    by the design's single count line.
 *  - Bulk selection. The legacy tab does not offer it in this context either —
 *    `showBulkActions` is left unset by `AccountVirtualCards` — so nothing is
 *    lost, and `CardVirtualCardsTable` has no checkbox column.
 *  - The `readOnly` empty-state variant, which only ever fed `TableEmptyState`.
 *    This table's empty state says the same thing without branching on
 *    ownership, and the create CTA is withheld by permission instead.
 *
 * `useFilters` stays even though nothing on the tab changes a filter: it is what
 * builds the `Account`-context query params — the `creditCardId` clause and the
 * sort defaults live in `makeBaseVirtualCardsSchema`'s `finalizeApi` — so this
 * tab and the legacy one request identical rows.
 *
 * Routing stays in `AccountEntityPage`; this page owns its data and builds the
 * virtual-card destination, and both features stay routing-free.
 */
export default function AccountVirtualCardsTab(
  props: AccountVirtualCardsTabProps,
): ReactElement {
  const { creditCardId } = props
  const history = useHistory()
  // Resize-aware, like the legacy tab: the grid is sized in pixels, so a window
  // resize has to re-measure or the table keeps its old height.
  const [, windowHeight] = useWindowSize()
  // What should stay painted behind the create-card flyout. Read here because
  // the flyout's own route declares `homeUrl()` as its default background, so a
  // push that does not carry this sends the user's page out from under them.
  const backgroundLocation = useSelector(selectCalculatedBackgroundLocation)
  /*
    The card's real counts, not the rows loaded so far.

    Both are populated as a side effect of the credit-card detail query the
    parent page already runs, so reading them costs no request. Paging over the
    loaded count instead would rewrite the heading as the user scrolls — "50
    virtual cards", then "100" — for one unchanged card.

    The design's count line is "N active · M total", so the tab needs the active
    tally as well as the total; legacy read the same pair for its ObjectBoxTitle.
  */
  const totalVirtualCardCount = useSelector(selectCreditCardStatisticsVcnCount)
  const activeVirtualCardCount = useSelector(
    selectCreditCardStatisticsActiveVcnCount,
  )

  const { data: creditCardPermissions } = useQueryCreditCardPermissions({
    creditCardId,
    retry: false,
  })

  const { apiQueryParams } = useFilters<
    VirtualCardsFilters,
    VirtualCardsFiltersBody
  >(
    useMemo(
      () =>
        makeBaseVirtualCardsSchema({
          // The same context the legacy tab passes. It selects the sort defaults
          // and the `creditCardId` clause in `finalizeApi`, so changing it here
          // would change which cards come back.
          context: VirtualCardsListContext.Account,
          creditCardId,
        }),
      [creditCardId],
    ),
  )

  const {
    data,
    isPending,
    isError,
    isFetching,
    hasNextPage,
    fetchNextPage,
    complete,
  } = useQueryVirtualCards({
    filters: apiQueryParams,
    perPage: VIRTUAL_CARDS_PER_PAGE,
    // Overrides the list factory's `placeholderData: keepPreviousData`. This tab
    // renders from a keyless <Route>, so moving between two cards re-renders
    // rather than remounts, and the previous card's rows would paint under the
    // new card's heading with `isPending` already false. Same override, same
    // reason, as the Overview snapshot.
    placeholderData: undefined,
  })

  const virtualCards = useMemo(
    () => data?.virtualCards ?? [],
    [data?.virtualCards],
  )

  // `fetch` is called by the grid when it reaches the end of the loaded rows.
  // The page argument is ignored: the infinite query owns its own cursor, and
  // taking one from the caller is how the two get out of step.
  const fetchMore = useCallback((): void => {
    if (!hasNextPage || isFetching) return
    void fetchNextPage()
  }, [hasNextPage, isFetching, fetchNextPage])

  /*
    Carries `backgroundLocation` explicitly. The create-card route declares a
    default background of `homeUrl()`, so pushing a bare path opens the flyout
    over HOME — this tab visibly disappears behind it, and dismissing the flyout
    leaves the user on the dashboard instead of the card they started from. Same
    contract the Overview snapshot's CTA and `AccountBudgetsTab`'s follow.
  */
  const handleCreateVirtualCard = useCallback((): void => {
    history.push({
      pathname: createVirtualCardUrl(),
      search: '',
      hash: '',
      state: { backgroundLocation },
    })
  }, [history, backgroundLocation])

  const handleSelectVirtualCard = useCallback(
    (virtualCardId: string): void => {
      history.push(virtualCardDetailUrl(virtualCardId))
    },
    [history],
  )

  return (
    // Every sibling tab supplies its own BackgroundPageContent — that is where
    // the page gutters live.
    <BackgroundPageContent>
      <div data-testid="accountVirtualCardsTab-content">
        <CardVirtualCardsTable
          virtualCards={virtualCards}
          isPending={isPending}
          // Without this a failed request reads as an empty one, and the table
          // would tell the user a card with cards has none.
          isError={isError}
          totalCount={totalVirtualCardCount}
          activeCount={activeVirtualCardCount}
          // Rows are real links; the page owns the URL, the table owns the
          // click.
          virtualCardHref={virtualCardDetailUrl}
          onSelectVirtualCard={handleSelectVirtualCard}
          onCreateVirtualCard={
            creditCardPermissions?.canIssueVirtualCards === true
              ? handleCreateVirtualCard
              : undefined
          }
          infiniteScrollProps={{
            fetch: fetchMore,
            loading: isFetching,
            complete,
            hasNextPage,
          }}
          height={Math.max(windowHeight - PAGE_CHROME_HEIGHT, 0)}
        />
      </div>
    </BackgroundPageContent>
  )
}
